import { DingdingOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, Modal } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CurrentUser } from '@mori/contracts';
import { api, post } from '../api';
import { BRAND, BrandMark } from '../brand';
import { withLoginTimeout } from '../login-timeout';
import './eam-login.css';

export function LoginPage({ onLogin, autoLogin = true }: { onLogin: (user: CurrentUser) => void; autoLogin?: boolean }) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [localOpen, setLocalOpen] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [dingError, setDingError] = useState(new URLSearchParams(window.location.search).has('loginError') ? '钉钉登录失败，请确认帐号已开通、身份在职，或重新授权。' : '');
  const [loginOptions, setLoginOptions] = useState<{ dingtalkEnabled: boolean; embeddedEnabled: boolean }>();
  const attemptedAutoLogin = useRef(false);
  const loginInFlight = useRef(false);
  const embedded = /DingTalk/i.test(navigator.userAgent);
  useEffect(() => { let active = true; void api<{ dingtalkEnabled: boolean; embeddedEnabled: boolean }>('/auth/login-options').then(result => { if (active) setLoginOptions(result); }).catch(() => { if (active) setDingError('无法读取登录配置，请刷新重试'); }); return () => { active = false; }; }, []);
  const dingLogin = useCallback(async () => {
    // Guard synchronously: state updates alone cannot prevent two challenges
    // from replacing the same browser cookie (including StrictMode effects).
    if (loginInFlight.current) return;
    loginInFlight.current = true;
    setLoading(true); setDingError('');
    try {
      if (embedded) {
        const bootstrap = await withLoginTimeout(signal => api<{ state: string; corpId: string; clientId: string }>('/auth/dingtalk/embedded', { signal }));
        const { requestEmbeddedCode } = await import('../dingtalk-bridge');
        const code = await requestEmbeddedCode(bootstrap.corpId, bootstrap.clientId);
        const result = await withLoginTimeout(signal => api<{ user: CurrentUser }>('/auth/dingtalk/embedded', { method: 'POST', body: JSON.stringify({ code, state: bootstrap.state }), signal }));
        onLogin(result.user);
      } else {
        const result = await api<{ url: string }>(`/auth/dingtalk/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        window.location.assign(result.url);
      }
    } catch (error) { setDingError(error instanceof Error && error.name !== 'TimeoutError' ? error.message : '钉钉登录响应超时或失败，请重试'); }
    finally { loginInFlight.current = false; setLoading(false); }
  }, [embedded, onLogin]);

  useEffect(() => {
    if (!autoLogin || !embedded || !loginOptions?.embeddedEnabled || attemptedAutoLogin.current) return;
    attemptedAutoLogin.current = true;
    void dingLogin();
  }, [autoLogin, embedded, loginOptions?.embeddedEnabled, dingLogin]);

  const passwordLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    setLoginError('');
    try {
      const result = await post<{ user: CurrentUser }>('/auth/login', values);
      message.success('登录成功');
      onLogin(result.user);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : '登录失败');
    } finally {
      setLoading(false);
    }
  };

  return <div className="eam-login-page">
    <main className="eam-login-shell">
      <section className="eam-login-hero" aria-labelledby="eam-login-hero-title">
        <header className="eam-login-brand" aria-label={`${BRAND.systemName}品牌`}>
          <span className="eam-login-brand-mark"><BrandMark size={40} /></span>
          <div className="eam-login-brand-copy"><strong>{BRAND.companyName}</strong><span>{BRAND.englishName}</span></div>
        </header>
        <div className="eam-login-hero-copy">
          <p className="eam-login-eyebrow"><span aria-hidden="true" />设备管理中枢</p>
          <h1 id="eam-login-hero-title">让设备管理<br /><span>更规范、更高效</span></h1>
          <p className="eam-login-hero-description">连接点检、保养与维修数据，为设备全生命周期提供统一的管理入口。</p>
          <ol className="eam-login-capabilities" aria-label="系统核心能力">
            <li><span>01</span><strong>设备点检</strong></li>
            <li><span>02</span><strong>设备保养</strong></li>
            <li><span>03</span><strong>设备维修</strong></li>
          </ol>
        </div>
      </section>
      <section className="eam-login-access-panel" aria-label="企业身份认证">
        <section className="eam-login-card" aria-labelledby="eam-login-heading">
          <header className="eam-login-heading"><p className="eam-login-heading-kicker">企业身份认证</p><h2 id="eam-login-heading">墨睿科技EAM系统</h2></header>
          <div className="eam-login-primary-action">
            {dingError && <Alert type="error" title={dingError} showIcon />}
            <Button type="primary" size="large" block icon={<DingdingOutlined />} loading={loading} disabled={!loginOptions || !(embedded ? loginOptions.embeddedEnabled : loginOptions.dingtalkEnabled)} onClick={() => void dingLogin()}>{embedded ? (loading ? '正在使用钉钉身份登录…' : dingError ? '重试钉钉免登' : '使用当前钉钉身份登录') : '使用钉钉登录'}</Button>
            {loginOptions && !(embedded ? loginOptions.embeddedEnabled : loginOptions.dingtalkEnabled) && <Alert type="info" title="钉钉登录尚未配置，请联系管理员。" />}
            <p><SafetyCertificateOutlined aria-hidden="true" />仅限企业授权人员访问</p>
          </div>
          <div className="eam-login-secondary-entry"><button type="button" disabled={loading} onClick={() => { setLoginError(''); setLocalOpen(true); }}>应急管理员登录 <span aria-hidden="true">→</span></button></div>
        </section>
        <footer className="eam-login-footer">© 2026 {BRAND.companyName}</footer>
      </section>
    </main>
    <Modal rootClassName="eam-login-local-modal-root" className="eam-login-local-modal" title={<span className="eam-login-local-title"><SafetyCertificateOutlined />管理员登录</span>} width={400} open={localOpen} footer={null} destroyOnHidden onCancel={() => { if (!loading) setLocalOpen(false); }}>
      <p className="eam-login-local-description">请使用已授权的应急管理员登录。</p>
      {loginError && <Alert className="eam-login-alert" type="error" title={loginError} showIcon />}
      <Form className="eam-login-local-form" layout="vertical" onFinish={passwordLogin} requiredMark={false}>
        <Form.Item name="username" label="管理员账号" rules={[{ required: true, whitespace: true, message: '请输入管理员账号' }]}><Input autoComplete="username" placeholder="请输入管理员账号" /></Form.Item>
        <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}><Input.Password autoComplete="current-password" placeholder="请输入密码" /></Form.Item>
        <Button className="eam-login-local-submit" htmlType="submit" type="primary" block loading={loading}>登录系统</Button>
      </Form>
    </Modal>
  </div>;
}
